import { TrashIcon } from "@heroicons/react/solid";
import React, { useCallback, useRef, useState, useMemo } from "react";
import { useDrag, useDrop } from 'react-dnd';
import { classNames } from "../../../../../helpers/classNames";
import { LuGripVertical, LuPalette, LuChevronDown } from "react-icons/lu";
import SearchableColumnSelect from "../../shared/SearchableColumnSelect";

const formatOperators = {
    single_select: ["is", "is not", "is not empty", "is empty"],
    multi_select: ["includes", "does not include", "is not empty", "is empty"],
    household_link: ["is not empty", "is empty", "contains", "does not contain", 'is exactly', 'is not exactly'],
    link: ["is not empty", "is empty", "contains", "does not contain", 'is exactly', 'is not exactly'],
    claim_household: ["is not empty", "is empty", "contains", "does not contain", 'is exactly', 'is not exactly'],
    text: ["is not empty", "is empty", "contains", "does not contain", 'is exactly', 'is not exactly'],
    checkbox: ["checked", "not checked"],
    date: [
        "is not empty",
        "is empty",
        "is",
        "is not",
        "is before",
        "is after",
        "is on or before",
        "is on or after",
    ],
    due_date: ["is not empty", "is empty", "is", "is not", "is before", "is after", "is on or before", "is on or after"],
    number: ["is not empty", "is empty", "<", ">", "=", '<=', '>=', '!='],
    advisor: ["is not empty", "is empty", "is", "is not"],
};

const comparisonTextFilters = ['contains', 'does not contain', 'is exactly', 'is not exactly'];
const comparisonDateFilters = ["is", "is not", "is before", "is after", "is on or before", "is on or after"];
const comparisonNumberFilters = ["<", ">", "=", '<=', '>=', '!='];
const comparisonSelectFilters = ["is", "is not", "includes", "does not include"];

// Date comparison options
const dateComparisonOptions = [
    { value: "today", label: "Today" },
    { value: "tomorrow", label: "Tomorrow" },
    { value: "yesterday", label: "Yesterday" },
    { value: "one_week_ago", label: "One week ago" },
    { value: "one_week_from_now", label: "One week from now" },
    { value: "one_month_ago", label: "One month ago" },
    { value: "one_month_from_now", label: "One month from now" },
    { value: "number_of_days_ago", label: "Number of days ago" },
    { value: "number_of_days_from_now", label: "Number of days from now" },
    { value: "exact_date", label: "Exact date" },
];

// Predefined color options
const colorOptions = [
    { name: "Blue", bg: "#3B82F6", text: "#FFFFFF" },
    { name: "Red", bg: "#EF4444", text: "#FFFFFF" },
    { name: "Green", bg: "#10B981", text: "#FFFFFF" },
    { name: "Yellow", bg: "#F59E0B", text: "#000000" },
    { name: "Purple", bg: "#8B5CF6", text: "#FFFFFF" },
    { name: "Pink", bg: "#EC4899", text: "#FFFFFF" },
    { name: "Orange", bg: "#F97316", text: "#FFFFFF" },
    { name: "Gray", bg: "#6B7280", text: "#FFFFFF" },
    { name: "Light Blue", bg: "#DBEAFE", text: "#1E40AF" },
    { name: "Light Red", bg: "#FEE2E2", text: "#991B1B" },
    { name: "Light Green", bg: "#D1FAE5", text: "#065F46" },
    { name: "Light Yellow", bg: "#FEF3C7", text: "#92400E" },
];

const SmartTableConditionalFormattingRow = ({
    allRules,
    onRulesChange,
    columns,
    rule,
    thisRuleIndex,
    moveRule,
    advisors,
}) => {
    const ref = useRef(null);
    const [showColorPicker, setShowColorPicker] = useState(false);
    const colorPickerRef = useRef(null);

    const [{ handlerId }, drop] = useDrop({
        accept: 'formatting-rule',
        collect(monitor) {
            return {
                handlerId: monitor.getHandlerId(),
            };
        },
        hover(item, monitor) {
            if (!ref.current) {
                return;
            }
            const dragIndex = item.index;
            const hoverIndex = thisRuleIndex;

            if (dragIndex === hoverIndex) {
                return;
            }

            const hoverBoundingRect = ref.current?.getBoundingClientRect();
            const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
            const clientOffset = monitor.getClientOffset();
            const hoverClientY = clientOffset.y - hoverBoundingRect.top;

            if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
                return;
            }

            if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
                return;
            }

            moveRule(dragIndex, hoverIndex);
            item.index = hoverIndex;
        },
    });

    const [{ isDragging }, drag, preview] = useDrag({
        type: 'formatting-rule',
        item: () => {
            return { id: rule.col_id, index: thisRuleIndex };
        },
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
    });

    const opacity = isDragging ? 0.4 : 1;
    drag(drop(ref));

    const comparisonType = useMemo(() => {
        // For advisor, single_select, and multi_select columns, only show comparison for specific operators
        if (rule.col_type === "advisor" || rule.col_type === "single_select") {
            return ["is", "is not"].includes(rule.operator) ? "select" : null;
        }

        if (rule.col_type === "multi_select") {
            return ["includes", "does not include"].includes(rule.operator) ? "select" : null;
        }

        if (comparisonTextFilters.includes(rule.operator)) return "text";
        if (comparisonDateFilters.includes(rule.operator)) return "date";
        if (comparisonNumberFilters.includes(rule.operator)) return "number";
        if (comparisonSelectFilters.includes(rule.operator)) return "select";
        return null;
    }, [rule.operator, rule.col_type]);

    // Check if this operator needs a comparison value
    const needsComparison = useCallback(() => {
        const noComparisonOperators = ["is not empty", "is empty", "checked", "not checked"];
        return !noComparisonOperators.includes(rule.operator);
    }, [rule.operator]);

    const showComparison = needsComparison();

    // Parse the comparison value for date operations
    const parsedDateComparison = useMemo(() => {
        if (!rule.comparison || !comparisonDateFilters.includes(rule.operator)) {
            return { type: null, value: '' };
        }

        const comparisonStr = String(rule.comparison);

        // Handle the new format: "type:value" for days operations
        if (comparisonStr.includes(':') && (comparisonStr.startsWith('number_of_days_ago:') || comparisonStr.startsWith('number_of_days_from_now:'))) {
            const [type, value] = comparisonStr.split(':');
            return { type, value };
        }

        // Check if it looks like an ISO date string (exact date stored without prefix)
        if (comparisonStr.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/)) {
            return { type: 'exact_date', value: comparisonStr };
        }

        // Check if it's the exact_date placeholder
        if (comparisonStr === 'exact_date') {
            return { type: 'exact_date', value: '' };
        }

        // Check if it's one of our other predefined options
        const predefinedOption = dateComparisonOptions.find(opt => opt.value === comparisonStr);
        if (predefinedOption) {
            return { type: comparisonStr, value: '' };
        }

        // Legacy support - assume it's an exact date
        return { type: 'exact_date', value: comparisonStr };
    }, [rule.comparison, rule.operator]);

    const { type: dateComparisonType, value: dateComparisonValue } = parsedDateComparison;

    const handleUpdateRule = async ({ new_value: newValue, key }) => {
        // Note: Date conversion is now handled in handleDateComparisonUpdate for date comparisons
        // No need to convert dates here anymore since we use the structured format

        const newRule = { ...rule };
        const matchingColumn = columns.find(
            (col) => col.col_id === (key === "col_id" ? newValue : newRule.col_id)
        );

        switch (key) {
            case "col_id":
                newRule.col_id = newValue;
                newRule.col_type = matchingColumn?.col_type;
                newRule.select_options = matchingColumn?.select_options;
                break;
            case "operator":
                newRule.operator = newValue;
                break;
            case "comparison":
                newRule.comparison = newValue;
                break;
            case "background_color":
                newRule.background_color = newValue;
                break;
            case "text_color":
                newRule.text_color = newValue;
                break;
            default:
                break;
        }

        const newRules = [...allRules];
        newRules[thisRuleIndex] = newRule;
        onRulesChange(newRules);
    };

    // Helper function to update date comparison
    const handleDateComparisonUpdate = (type, value = '') => {
        let comparisonValue;

        // For predefined options without additional values
        if (['today', 'tomorrow', 'yesterday', 'one_week_ago', 'one_week_from_now', 'one_month_ago', 'one_month_from_now'].includes(type)) {
            comparisonValue = type;
        }
        // For number of days operations that need the type:value format
        else if (['number_of_days_ago', 'number_of_days_from_now'].includes(type)) {
            comparisonValue = value ? `${type}:${value}` : type;
        }
        // For exact dates, store the ISO date directly, or just "exact_date" if no date yet
        else if (type === 'exact_date') {
            comparisonValue = value || 'exact_date';
        }
        else {
            comparisonValue = type;
        }

        handleUpdateRule({ new_value: comparisonValue, key: "comparison" });
    };

    const handleRemoveRule = async () => {
        try {
            const newRules = allRules.filter((_, index) => index !== thisRuleIndex);
            onRulesChange(newRules);
        } catch (error) {
            console.error("Error removing rule:", error);
        }
    };

    const handleColorSelect = (colorOption) => {
        const newRule = { ...rule };
        newRule.background_color = colorOption.bg;
        newRule.text_color = colorOption.text;

        const newRules = [...allRules];
        newRules[thisRuleIndex] = newRule;
        onRulesChange(newRules);
        setShowColorPicker(false);
    };

    // Close color picker when clicking outside
    React.useEffect(() => {
        const handleClickOutside = (event) => {
            if (colorPickerRef.current && !colorPickerRef.current.contains(event.target)) {
                setShowColorPicker(false);
            }
        };

        if (showColorPicker) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }
    }, [showColorPicker]);

    const nonFormattableColumns = ["button", "copy_to_clipboard"];

    return (
        <div
            ref={ref}
            style={{ opacity }}
            data-handler-id={handlerId}
            className={classNames(
                "transition-opacity duration-200",
                isDragging && "cursor-grabbing"
            )}
        >
            <div className="bg-white border border-gray-200 rounded-lg">
                <div className="flex items-stretch divide-x divide-gray-200">
                    {/* Rule Order Indicator */}
                    <div className="px-2 py-2 flex items-center justify-center">
                        <div className="w-5 h-5 bg-rose-100 text-rose-700 rounded-full flex items-center justify-center text-xs font-semibold">
                            {thisRuleIndex + 1}
                        </div>
                    </div>

                    {/* Drag Handle */}
                    <div
                        className="px-2 py-2 cursor-grab hover:bg-gray-50 flex items-center justify-center transition-colors duration-150"
                        style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
                    >
                        <LuGripVertical className="w-4 h-4 text-gray-400" />
                    </div>

                    {/* Column Selection */}
                    <div className="flex-1 min-w-[180px] overflow-visible">
                        <SearchableColumnSelect
                            columns={columns || []}
                            value={rule.col_id}
                            onChange={(colId) => handleUpdateRule({ new_value: colId, key: "col_id" })}
                            excludeTypes={nonFormattableColumns}
                            placeholder="Select column..."
                            className="border-none shadow-none rounded-none focus:ring-0 focus:border-transparent text-sm"
                        />
                    </div>

                    {/* Operator Selection */}
                    <div className="flex-1 min-w-[120px] overflow-visible">
                        <select
                            name="operator_options"
                            id="operator_options"
                            value={rule.operator || ""}
                            onChange={(e) => {
                                handleUpdateRule({ new_value: e.target.value, key: "operator" });
                            }}
                            className="w-full h-full px-2 bg-transparent text-gray-800 border-none focus:ring-0 focus:outline-none text-sm"
                        >
                            {formatOperators[rule.col_type]?.map((operator) => (
                                <option value={operator} key={operator}>
                                    {operator}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Comparison Value */}
                    {showComparison && (
                        <div className="flex-1 min-w-[160px] px-2 py-1 overflow-visible">
                            {comparisonType === "date" ? (
                                <div className="space-y-1">
                                    {/* Date comparison type selector */}
                                    <select
                                        value={dateComparisonType || ""}
                                        onChange={(e) => {
                                            const newType = e.target.value;
                                            handleDateComparisonUpdate(newType, '');
                                        }}
                                        className="w-full px-2 bg-transparent text-gray-800 border-none focus:ring-0 focus:outline-none text-sm"
                                    >
                                        <option value="">Select comparison</option>
                                        {dateComparisonOptions.map((option) => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>

                                    {/* Additional input for number of days */}
                                    {(dateComparisonType === 'number_of_days_ago' || dateComparisonType === 'number_of_days_from_now') && (
                                        <input
                                            type="number"
                                            value={dateComparisonValue || ''}
                                            onChange={(e) => {
                                                handleDateComparisonUpdate(dateComparisonType, e.target.value);
                                            }}
                                            placeholder="Enter number of days"
                                            className="w-full px-2 bg-transparent text-gray-800 border-none focus:ring-0 focus:outline-none text-sm"
                                            min="0"
                                        />
                                    )}

                                    {/* Date picker for exact date */}
                                    {dateComparisonType === 'exact_date' && (
                                        <input
                                            type="date"
                                            value={dateComparisonValue ? (() => {
                                                try {
                                                    // If it's an ISO string, extract the date part
                                                    const dateStr = String(dateComparisonValue);
                                                    if (dateStr.includes('T')) {
                                                        return dateStr.split('T')[0];
                                                    }
                                                    // If it's already in YYYY-MM-DD format
                                                    return dateStr.substring(0, 10);
                                                } catch (e) {
                                                    return '';
                                                }
                                            })() : ''}
                                            onChange={(e) => {
                                                const dateStr = e.target.value;
                                                if (!dateStr) {
                                                    // If date is cleared, just update with the type
                                                    handleDateComparisonUpdate(dateComparisonType, '');
                                                    return;
                                                }

                                                const [year, month, day] = dateStr.split('-').map(Number);
                                                // Validate the date components
                                                if (isNaN(year) || isNaN(month) || isNaN(day)) {
                                                    return;
                                                }

                                                try {
                                                    const isoDate = new Date(Date.UTC(year, month - 1, day, 0, 0, 0)).toISOString();
                                                    handleDateComparisonUpdate(dateComparisonType, isoDate);
                                                } catch (e) {
                                                    console.error('Date conversion error:', e);
                                                }
                                            }}
                                            className="w-full px-2 bg-transparent text-gray-800 border-none focus:ring-0 focus:outline-none text-sm"
                                        />
                                    )}
                                </div>
                            ) : comparisonType !== "select" ? (
                                <input
                                    type={comparisonType || "text"}
                                    value={rule.comparison || ''}
                                    onChange={(e) => {
                                        handleUpdateRule({ new_value: e.target.value, key: "comparison" });
                                    }}
                                    placeholder="Enter a value"
                                    className="w-full px-2 bg-transparent text-gray-800 border-none focus:ring-0 focus:outline-none text-sm"
                                />
                            ) : (
                                <select
                                    value={rule.comparison || ""}
                                    onChange={(e) => {
                                        handleUpdateRule({ new_value: e.target.value, key: "comparison" });
                                    }}
                                    className="w-full px-2 bg-transparent text-gray-800 border-none focus:ring-0 focus:outline-none text-sm"
                                >
                                    <option value="">Select option</option>

                                    {rule.col_type === "advisor" &&
                                        advisors?.map((advisor) => (
                                            <option key={advisor.user_id} value={advisor.user_id}>
                                                {advisor.first_name} {advisor.last_name}
                                            </option>
                                        ))}
                                    {(rule.col_type === "single_select" || rule.col_type === 'multi_select') &&
                                        rule.select_options?.map((option) => (
                                            <option key={option.value} value={option.value}>{option.value}</option>
                                        ))}
                                </select>
                            )}
                        </div>
                    )}

                    {/* Color Selection */}
                    <div className="px-2 py-1 relative min-w-[100px] overflow-visible" ref={colorPickerRef}>
                        {/* Color Trigger Button */}
                        <button
                            type="button"
                            onClick={() => setShowColorPicker(!showColorPicker)}
                            className="w-full flex items-center justify-between px-2 py-1 h-full border border-gray-300 rounded hover:border-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-150"
                        >
                            <div className="flex items-center space-x-1">
                                <div
                                    className="w-3 h-3 rounded border border-gray-200 flex-shrink-0"
                                    style={{ backgroundColor: rule.background_color }}
                                />
                                <span
                                    className="text-xs font-medium px-1 rounded"
                                    style={{
                                        backgroundColor: rule.background_color,
                                        color: rule.text_color,
                                    }}
                                >
                                    Aa
                                </span>
                            </div>
                            <LuChevronDown className={classNames(
                                "w-3 h-3 text-gray-500 transition-transform duration-200",
                                showColorPicker && "transform rotate-180"
                            )} />
                        </button>

                        {/* Color Picker Dropdown */}
                        {showColorPicker && (
                            <div className="absolute top-full right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 p-3 w-[250px]">
                                {/* Predefined Colors */}
                                <div className="mb-3">
                                    <h4 className="text-xs font-medium text-gray-700 mb-2">Quick Colors</h4>
                                    <div className="grid grid-cols-6 gap-1.5">
                                        {colorOptions.map((color) => (
                                            <button
                                                key={color.name}
                                                type="button"
                                                onClick={() => handleColorSelect(color)}
                                                className={classNames(
                                                    "w-8 h-8 rounded border-2 flex items-center justify-center text-xs font-medium transition-all duration-150 hover:scale-110",
                                                    rule.background_color === color.bg && rule.text_color === color.text
                                                        ? "border-blue-500 ring-2 ring-blue-200"
                                                        : "border-gray-200 hover:border-gray-300"
                                                )}
                                                style={{
                                                    backgroundColor: color.bg,
                                                    color: color.text,
                                                }}
                                                title={color.name}
                                            >
                                                Aa
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Custom Colors */}
                                <div className="border-t border-gray-100 pt-3">
                                    <h4 className="text-xs font-medium text-gray-700 mb-2">Custom Colors</h4>
                                    <div className="flex space-x-2">
                                        <div className="flex-1">
                                            <label className="block text-xs text-gray-600 mb-1">Background</label>
                                            <div className="relative">
                                                <input
                                                    type="color"
                                                    value={rule.background_color}
                                                    onChange={(e) => {
                                                        handleUpdateRule({ new_value: e.target.value, key: "background_color" });
                                                    }}
                                                    className="w-full h-8 rounded border border-gray-300 cursor-pointer"
                                                />
                                            </div>
                                        </div>
                                        <div className="flex-1">
                                            <label className="block text-xs text-gray-600 mb-1">Text</label>
                                            <div className="relative">
                                                <input
                                                    type="color"
                                                    value={rule.text_color}
                                                    onChange={(e) => {
                                                        handleUpdateRule({ new_value: e.target.value, key: "text_color" });
                                                    }}
                                                    className="w-full h-8 rounded border border-gray-300 cursor-pointer"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Delete Button */}
                    <div className="flex items-center">
                        <button
                            onClick={handleRemoveRule}
                            className="px-3 py-2 text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors duration-150 h-full"
                            title="Remove formatting rule"
                        >
                            <TrashIcon className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SmartTableConditionalFormattingRow; 